/** Saved mail searches (2026-10-02), kept by the daemon so the browser and
 *  the Waki Mail app show the same list (GET/POST/PATCH/DELETE
 *  /api/mail/saved-searches). Each carries how many messages it finds and
 *  how many are unread, from the header cache.
 *
 *  Every change fires `wov:saved-searches` so the title bar's suggestions,
 *  Mail's Search section and the results' Save button all show it at once;
 *  remembering a search fires `wov:recent-searches`. */
import { useCallback, useEffect, useState } from "react";
import { csrfFetch } from "../../lib/csrfFetch";

export type SavedSearch = { id: string; name: string; query: string; position: number; total: number | null; unread: number | null };

const CHANGED = "wov:saved-searches";

export async function listSavedSearches(): Promise<SavedSearch[]> {
  try {
    const r = await fetch("/api/mail/saved-searches", { credentials: "same-origin" });
    const j = (await r.json().catch(() => null)) as { ok?: boolean; data?: { searches?: SavedSearch[] } } | null;
    return r.ok && j?.ok ? j.data?.searches ?? [] : [];
  } catch {
    return [];
  }
}

async function send(method: string, url: string, body?: unknown): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const r = await csrfFetch(url, { method, body: body === undefined ? undefined : JSON.stringify(body) });
    const j = (await r.json().catch(() => null)) as { ok?: boolean; error?: { message?: string; code?: string } } | null;
    if (r.ok && j?.ok) {
      window.dispatchEvent(new Event(CHANGED));
      return { ok: true };
    }
    return { ok: false, error: j?.error?.message ?? j?.error?.code ?? `HTTP ${r.status}` };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export const saveSearch = (name: string, query: string) => send("POST", "/api/mail/saved-searches", { name, query });
export const renameSavedSearch = (id: string, name: string) => send("PATCH", `/api/mail/saved-searches/${encodeURIComponent(id)}`, { name });
export const updateSavedSearchQuery = (id: string, query: string) => send("PATCH", `/api/mail/saved-searches/${encodeURIComponent(id)}`, { query });
export const deleteSavedSearch = (id: string) => send("DELETE", `/api/mail/saved-searches/${encodeURIComponent(id)}`);

/** Move a saved search one place up or down the list: renumber the list
 *  in its new order (positions may have gaps or ties after deletes). */
export async function moveSavedSearch(searches: SavedSearch[], id: string, dir: -1 | 1): Promise<{ ok: true } | { ok: false; error: string }> {
  const order = [...searches];
  const i = order.findIndex((x) => x.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= order.length) return { ok: true };
  [order[i], order[j]] = [order[j], order[i]];
  for (let k = 0; k < order.length; k++) {
    if (order[k].position === k) continue;
    try {
      const r = await csrfFetch(`/api/mail/saved-searches/${encodeURIComponent(order[k].id)}`, { method: "PATCH", body: JSON.stringify({ position: k }) });
      if (!r.ok) return { ok: false, error: `HTTP ${r.status}` };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
  }
  window.dispatchEvent(new Event(CHANGED));
  return { ok: true };
}

/** The saved searches, kept current: on any change, when the window comes
 *  back, and every two minutes (their counts move as mail arrives). */
export function useSavedSearches(): { searches: SavedSearch[]; reload: () => void } {
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const reload = useCallback(() => {
    void listSavedSearches().then(setSearches);
  }, []);
  useEffect(() => {
    reload();
    const onFocus = () => reload();
    window.addEventListener(CHANGED, reload);
    window.addEventListener("focus", onFocus);
    const t = window.setInterval(reload, 120_000);
    return () => {
      window.removeEventListener(CHANGED, reload);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(t);
    };
  }, [reload]);
  return { searches, reload };
}

/** The saved search a query is, if any (same words, any spacing). */
export function savedFor(searches: SavedSearch[], query: string): SavedSearch | undefined {
  const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  const q = norm(query);
  return searches.find((s) => norm(s.query) === q);
}
